import { Fragment, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Card, StatTile } from '../../components/ui/Card';
import { InfoTip } from '../../components/ui/InfoTip';
import { Link } from 'react-router-dom';
import { isConstant, Nil, Table, TGroupRow, THead, Th, Td, Tr } from '../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { useProbabilityModel, useRemovalEffects, useSignalCoverage, useSignalOpsHealth } from '../../hooks/useSignalOps';
import type { CoverageState, SignalCoverageRow, SignalProbabilityRow } from '../../api/signalOps';
import { formatNumber } from '../../utils/numberFormat';
import { humanize, rateComparison } from './format';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { channelLabel, signalLabel } from './labels';
import { liftVerdict } from './modelLift';

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

/** One label per signal across both tables, from the shared map in labels.ts. */
function label(id: string, fallback?: string): string {
  return signalLabel(id, fallback);
}

/** The two base rates on this page measure different populations; said once, next to each. */
const BASE_RATES_NOTE = (
  <div className="space-y-2">
    <p>
      <span className="font-medium">Account base rate</span> (what each signal is worth): accounts that booked a demo ÷
      every account with any signal. Counted per account, and the prior every signal estimate is pulled towards.
    </p>
    <p>
      <span className="font-medium">Path conversion rate</span> (what each channel is worth): the chance a touch path
      reaches “converted” in the Markov chain built from accounts’ ordered channel paths. Counted per path, and an
      account with several touch sequences has several paths, so the two are not meant to match.
    </p>
  </div>
);

/** Customer-side signals are the exception on an acquisition register: marked inline instead of a Side column. */
function SideMark({ side }: { side: string }) {
  if (side === 'acquisition') return null;
  return <span className="ml-1.5 text-body-xs text-text-tertiary">· {humanize(side).toLowerCase()} side</span>;
}

/** Postgres "2026-08-12 12:00:00+00" → an ISO string every browser parses. */
function isoOf(ts: string | null): string | null {
  if (!ts) return null;
  return ts.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
}

function count(v: number): ReactNode {
  return v ? formatNumber(v) : <Nil />;
}

/** A share that is exactly zero is "nothing", shown as the faint dot like every other empty cell. */
function share(v: number): ReactNode {
  return v ? pct(v) : <Nil />;
}

/**
 * The value at least half the rows share, so a column can stay blank on those rows and
 * name it once in its header. Null when nothing dominates.
 */
function majority<T>(rows: readonly T[], pick: (row: T) => string): string | null {
  if (rows.length < 2) return null;
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(pick(r), (counts.get(pick(r)) ?? 0) + 1);
  const [value, n] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]!;
  return n / rows.length >= 0.5 ? value : null;
}


const usedLabel = (r: SignalProbabilityRow) => (r.trusted ? 'Model' : 'Hand-set');

/** A card title with its method explanation in an (i) rather than a paragraph. */
function SectionTitle({ title, info }: { title: string; info: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <h3 className="text-body-md font-medium text-text-primary">{title}</h3>
      <InfoTip>{info}</InfoTip>
    </div>
  );
}

const STATE_ORDER: CoverageState[] = ['live', 'no_data', 'no_emitter'];

/** A short column header with its qualifier one click away, so long headers don't collide. */
function HeaderTip({ label: text, tip }: { label: string; tip: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {text}
      <InfoTip label={tip} className="normal-case font-normal">
        {tip}
      </InfoTip>
    </span>
  );
}

/**
 * A row's note, unless it only restates the numbers beside it: a live signal's note is
 * "N rows across M accounts", which the Ledger rows and Accounts columns already show.
 */
function rowNote(r: SignalCoverageRow): string | null {
  // The API writes its notes with an em dash; the UI says it with a colon.
  return r.state === 'live' ? null : r.note ? r.note.replace(/\s+—\s+/g, ': ') : null;
}

/** The note most rows in a group share — shown once on the group header instead of on every row. */
function commonNote(rows: SignalCoverageRow[]): string | null {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const note = rowNote(r);
    if (note) counts.set(note, (counts.get(note) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 1;
  for (const [note, n] of counts) {
    if (n > bestN) {
      best = note;
      bestN = n;
    }
  }
  return best;
}

/**
 * The credible interval, drawn. The point of showing it is that a signal seen
 * on three accounts and one seen on three hundred produce the same-looking
 * number and must not read the same.
 */
function Interval({ low, high, mean }: { low: number; high: number; mean: number }) {
  const scale = (v: number) => `${Math.min(100, Math.max(0, v * 100))}%`;
  return (
    <span className="inline-flex w-32 items-center" title={`94% credible: ${pct(low)} – ${pct(high)}`}>
      <span className="relative h-1.5 w-full rounded-full bg-surface-tertiary">
        <span
          className="absolute h-1.5 rounded-full bg-text-muted"
          style={{ left: scale(low), width: `${Math.max(1, (high - low) * 100)}%` }}
        />
        <span
          className="absolute h-1.5 w-0.5 bg-text-primary"
          style={{ left: scale(mean) }}
        />
      </span>
    </span>
  );
}

const COVERAGE_VARIANT: Record<CoverageState, 'success' | 'warning' | 'default'> = {
  live: 'success',
  no_data: 'warning',
  no_emitter: 'default',
};

const COVERAGE_LABEL: Record<CoverageState, string> = {
  live: 'Live',
  no_data: 'No data',
  no_emitter: 'No emitter',
};

/** Every account with the signal, any time: the number Signal coverage shows. */
function anyTime(r: SignalProbabilityRow): number {
  return r.accountsAnyTime ?? r.accountsWithSignal;
}

/**
 * A signal no account has produced: its estimate is the prior and its weight the hand-set one.
 * A live signal whose accounts all show it after booking (meeting held) is not one of these:
 * it stays in the table so the bridge to Coverage is visible.
 */
function usesDefaultPrior(r: SignalProbabilityRow): boolean {
  return anyTime(r) === 0 && !r.trusted;
}

/**
 * "Demo booked: 104 accounts have the signal; 17 before their first booking." One line for every
 * row where Accounts and Before differ, so each gap between this table and Coverage is explained.
 */
function bridgeLines(rows: SignalProbabilityRow[]): Array<{ id: string; text: string }> {
  return rows
    .filter((r) => anyTime(r) > 0 && anyTime(r) !== r.accountsWithSignal)
    .map((r) => {
      if (!r.isOutcome) {
        const after = anyTime(r) - r.accountsWithSignal;
        return {
          id: r.signalType,
          text: `${label(r.signalType)}: ${formatNumber(anyTime(r))} accounts have the signal = ${formatNumber(
            r.accountsWithSignal,
          )} before their first booking + ${formatNumber(after)} only at or after it.`,
        };
      }
      const n = anyTime(r);
      const before = r.accountsWithSignal;
      const tail =
        before === 0
          ? 'none before their first booking: it follows the conversion, so it cannot predict it.'
          : `${formatNumber(before)} before ${before === 1 ? 'its' : 'their'} first booking. It is the conversion or follows it, so it is excluded from the lift and keeps its hand-set weight.`;
      return {
        id: r.signalType,
        text: `${label(r.signalType)}: ${formatNumber(n)} account${n === 1 ? ' has' : 's have'} the signal; ${tail}`,
      };
    });
}

function ProbabilityRow({
  r,
  usedMajority,
  baseRate,
}: {
  r: SignalProbabilityRow;
  usedMajority: string | null;
  baseRate: number;
}) {
  const verdict = liftVerdict(r, baseRate);
  const liftTone = verdict.tone === 'good' ? 'text-success' : verdict.tone === 'bad' ? 'text-error' : 'text-text-tertiary';
  const inUse = r.isOutcome || !r.trusted ? r.currentWeight : r.derivedWeight;
  return (
    <Tr>
      <Td className="text-text-primary">
        {label(r.signalType)}
        {r.isOutcome && (
          <span
            className="ml-1.5 text-body-xs text-text-tertiary"
            title="The conversion itself or what follows it, not a predictor of it: excluded from the lift and the model weights."
          >
            · outcome
          </span>
        )}
      </Td>
      <Td align="right" className="tabular-nums text-text-tertiary">{count(anyTime(r))}</Td>
      <Td align="right" className="tabular-nums text-text-secondary">{count(r.accountsWithSignal)}</Td>
      <Td align="right" className="tabular-nums text-text-secondary">{count(r.convertedWithSignal)}</Td>
      <Td align="right" className="tabular-nums text-text-muted">
        {r.accountsWithSignal ? pct(r.observedRate) : <Nil />}
      </Td>
      {r.isOutcome ? (
        // The conversion and what follows it: a lift would measure the booking against itself.
        <>
          <Td align="right" title="Outcome: not modelled">
            <Nil />
          </Td>
          <Td title="Outcome: not modelled">
            <Nil />
          </Td>
          <Td align="right" title="Outcome: excluded from lift">
            <Nil />
          </Td>
        </>
      ) : (
        <>
          <Td align="right" className="tabular-nums text-text-primary">{pct(r.posteriorMean)}</Td>
          <Td>
            {r.accountsWithSignal ? (
              <Interval low={r.credibleLow} high={r.credibleHigh} mean={r.posteriorMean} />
            ) : (
              <Nil />
            )}
          </Td>
          <Td align="right">
            <span
              className={clsx('tabular-nums', liftTone)}
              title={verdict.why ?? undefined}
            >
              {r.liftRatio.toFixed(2)}×
            </span>
          </Td>
        </>
      )}
      <Td align="right" className="tabular-nums text-text-tertiary">{r.currentWeight}</Td>
      <Td align="right" className="whitespace-nowrap tabular-nums text-text-primary">
        {usedLabel(r) !== usedMajority && (
          <span className="mr-1.5 text-body-xs text-text-tertiary">{usedLabel(r) === 'Model' ? 'model' : 'hand-set'}</span>
        )}
        {inUse}
      </Td>
    </Tr>
  );
}

/**
 * The model, shown. Three things a reader needs and none of them is a single
 * number: what each signal is actually worth, what each channel is worth, and
 * which signals are not being captured at all.
 */
export function ModelView() {
  const probabilities = useProbabilityModel();
  const removal = useRemovalEffects();
  const coverage = useSignalCoverage();
  const health = useSignalOpsHealth();
  const [showPriorRows, setShowPriorRows] = useState(false);
  // Groups with no live data (No data, No emitter) are collapsed to one summary row each.
  const [openStates, setOpenStates] = useState<ReadonlySet<CoverageState>>(new Set());
  const coverageRows = coverage.data?.rows ?? [];
  const sideConstant = isConstant(coverageRows, (r) => r.side);
  // lemlist email emitters that exist but wrote nothing: an exception, not a quiet "No data" row.
  const emptyLemlist = coverageRows.filter((r) => r.state === 'no_data' && r.channel === 'email-outbound');
  const probRows = probabilities.data?.rows ?? [];
  const priorRows = probRows.filter(usesDefaultPrior);
  // Only collapse when it saves something; a single prior row stays in place.
  const collapsePrior = priorRows.length > 1;
  const evidenceRows = collapsePrior ? probRows.filter((r) => !usesDefaultPrior(r)) : probRows;
  // Over the rows on screen: the collapsed default-prior rows all keep the hand-set weight and say so.
  const usedMajority = majority(evidenceRows, usedLabel);
  const bridge = bridgeLines(probRows);
  // Data health's "with a signal" (any signal, any time), so the two numbers are reconciled in the text.
  const anySignalAccounts = health.data?.coverage.withSignals ?? null;

  const cov = coverage.data;
  // Channels with touch paths but no live signal of their own: their touches are website
  // visits the channel resolver books to them by UTM source (a newsletter link, an ad).
  const liveChannels = new Set(coverageRows.filter((r) => r.state === 'live').map((r) => r.channel));
  const utmOnlyPaths = new Map(
    (removal.data?.effects ?? []).filter((e) => e.paths > 0 && !liveChannels.has(e.channel)).map((e) => [e.channel, e.paths]),
  );
  const utmNote = (r: SignalCoverageRow): string | null => {
    const paths = r.state === 'live' ? undefined : utmOnlyPaths.get(r.channel);
    return paths
      ? `${channelLabel(r.channel)} still has ${formatNumber(paths)} touch path${paths === 1 ? '' : 's'} below: website visits that arrived through its links (UTM), not this signal`
      : null;
  };
  const staleDays = cov?.staleDays ?? 7;
  const tileValue = (v: number | undefined) =>
    coverage.isError ? 'Unavailable' : cov && v !== undefined ? formatNumber(v) : '…';
  const registered = cov ? cov.rows.length : 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile
          label="Live"
          value={tileValue(cov?.live)}
          sub={
            cov
              ? `of ${registered} registered signals write rows${
                  cov.stale ? ` · ${cov.stale} quiet for ${cov.staleDays ?? 7}+ days` : ''
                }`
              : 'Signal register · ledger'
          }
        />
        <StatTile
          label="No data"
          value={tileValue(cov?.noData)}
          sub="Emitter exists, has written nothing · signal ledger"
          tooltip={
            cov?.silent !== undefined
              ? `Silent emitters, the count Data health shows: ${cov.silent} = ${cov.noData} never wrote + ${cov.stale ?? 0} quiet for ${cov.staleDays ?? 7}+ days.`
              : undefined
          }
        />
        <StatTile
          label="No emitter"
          value={tileValue(cov?.noEmitter)}
          sub="Registered, nothing writes it yet · weight is a placeholder"
        />
      </div>

      {/* ── Coverage ─────────────────────────────────────────────────── */}
      <Card padding="sm" className="space-y-3">
        <SectionTitle
          title="Signal coverage"
          info="Which of the registered signals can actually produce a row. A weight next to a signal that nothing writes reads exactly like a working one, so the gap is stated rather than implied."
        />

        {coverage.isLoading ? (
          <LoadingState />
        ) : coverage.isError ? (
          <ErrorState title="Couldn’t load coverage" error={coverage.error} onRetry={() => coverage.refetch()} />
        ) : coverage.data ? (
          <>
            {sideConstant && coverage.data.rows[0] && (
              <p className="text-body-xs text-text-tertiary">
                All on the {humanize(coverage.data.rows[0].side).toLowerCase()} side
              </p>
            )}

            {emptyLemlist.length > 0 && (
              <p className="text-body-sm text-warning-dark">
                lemlist email emitters return no data: {emptyLemlist.map((r) => label(r.signalType, r.label)).join(', ')}.
                The emitter exists, so the lemlist activity sync is empty or nothing resolves to a company.{' '}
                <Link to="/integrations" className="underline underline-offset-2">
                  Check lemlist in Integrations
                </Link>
              </p>
            )}

            <Table minWidth={760} colWidths={['auto', '12rem', '5rem', '7rem', '6rem']}>
              <THead>
                <tr>
                  <Th>Signal</Th>
                  <Th>Home channel</Th>
                  <Th align="right">Weight</Th>
                  <Th align="right">Ledger rows</Th>
                  <Th align="right">Accounts</Th>
                </tr>
              </THead>
              <tbody>
                {STATE_ORDER.map((state) => {
                  const group = coverage.data!.rows.filter((r) => r.state === state);
                  if (group.length === 0) return null;
                  const shared = commonNote(group);
                  const quiet = group.filter((r) => r.silence === 'stale').length;
                  const collapsible = state !== 'live';
                  const expanded = !collapsible || openStates.has(state);
                  const summary = `${group.length} signal${group.length === 1 ? '' : 's'}${
                    quiet > 0 ? ` · ${quiet} quiet for ${staleDays}+ days, marked` : ''
                  }${shared ? ` · ${shared}` : ''}`;
                  return (
                    <Fragment key={state}>
                      {collapsible ? (
                        <tr
                          className="cursor-pointer border-b border-border-light bg-surface-secondary hover:bg-surface-secondary/70"
                          onClick={() =>
                            setOpenStates((cur) => {
                              const next = new Set(cur);
                              if (next.has(state)) next.delete(state);
                              else next.add(state);
                              return next;
                            })
                          }
                          aria-expanded={expanded}
                        >
                          <td colSpan={5} className="py-2 px-3">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                              <ChevronRight
                                className={clsx('h-3.5 w-3.5 text-text-tertiary transition-transform', expanded && 'rotate-90')}
                                aria-hidden
                              />
                              <Badge variant={COVERAGE_VARIANT[state]}>{COVERAGE_LABEL[state]}</Badge>
                              <span className="text-body-xs text-text-tertiary">
                                {summary} · {expanded ? 'hide' : 'show'} the list
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <TGroupRow
                          colSpan={5}
                          label={<Badge variant={COVERAGE_VARIANT[state]}>{COVERAGE_LABEL[state]}</Badge>}
                          summary={summary}
                        />
                      )}
                      {expanded && group.map((r) => (
                        <Tr key={r.signalType}>
                          <Td>
                            <p className="text-text-primary">
                              {label(r.signalType, r.label)}
                              {!sideConstant && <SideMark side={r.side} />}
                            </p>
                            {rowNote(r) && rowNote(r) !== shared && (
                              <p className="text-body-xs text-text-tertiary">{rowNote(r)}</p>
                            )}
                            {utmNote(r) && <p className="text-body-xs text-text-tertiary">{utmNote(r)}</p>}
                            {r.silence === 'stale' && (
                              <p className="text-body-xs text-warning-dark">
                                Quiet for {staleDays}+ days · newest row {r.newestAt ? formatDayMonthYear(isoOf(r.newestAt)!) : 'none'}
                              </p>
                            )}
                          </Td>
                          <Td className="text-text-secondary">{channelLabel(r.channel)}</Td>
                          <Td align="right" className="tabular-nums text-text-secondary">{r.weight}</Td>
                          <Td align="right" className="tabular-nums text-text-secondary">{count(r.ledgerRows)}</Td>
                          <Td align="right" className="tabular-nums text-text-tertiary">{count(r.accounts)}</Td>
                        </Tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </>
        ) : null}
      </Card>

      {/* ── Probabilities ────────────────────────────────────────────── */}
      <Card padding="sm" className="space-y-3">
        <SectionTitle
          title="What each signal is worth"
          info={
            <>
              P(books a demo | produced this signal), estimated with a prior centred on the population rate and worth 30
              pseudo-observations. The bar is the 94% credible interval with the estimate marked: a signal seen on three
              accounts and one seen on three hundred produce similar-looking numbers and must not be read the same way.
              The model adjusts the hand-set weight within a band rather than replacing it.
            </>
          }
        />

        {probabilities.isLoading ? (
          <LoadingState />
        ) : probabilities.isError ? (
          <ErrorState title="Couldn’t load the model" error={probabilities.error} onRetry={() => probabilities.refetch()} />
        ) : probabilities.data ? (
          probabilities.data.conversions === 0 ? (
            <EmptyState
              title="No conversions on the ledger yet"
              description="Every signal keeps its hand-set weight until demo bookings are resolved to canonical accounts."
              compact
            />
          ) : (
            <>
              <p className="text-body-sm text-text-secondary">
                <span>
                  Account base rate <span className="tabular-nums font-medium">{pct(probabilities.data.baseRate)}</span>:{' '}
                  {formatNumber(probabilities.data.conversions)} of {formatNumber(probabilities.data.population)}{' '}
                  accounts with a signal before their first demo booked one
                  {anySignalAccounts !== null && anySignalAccounts > probabilities.data.population
                    ? ` (Data health counts ${formatNumber(anySignalAccounts)} with any signal: the other ${formatNumber(
                        anySignalAccounts - probabilities.data.population,
                      )} only have signals from their booking onward, or only outcome signals)`
                    : ''}
                  . Per account; the channel table below counts per touch path.
                </span>
                <InfoTip label="The two base rates, and why the counts differ" className="ml-1 align-middle">
                  {BASE_RATES_NOTE}
                  {bridge.length > 0 && (
                    <span className="mt-2 block">
                      Coverage counts every account with a signal; the estimate only counts a signal that came before the
                      account’s first demo booking.
                      {bridge.map((b) => (
                        <span key={b.id} className="mt-1 block">
                          {b.text}
                        </span>
                      ))}
                    </span>
                  )}
                </InfoTip>
              </p>
              <Table
                minWidth={1060}
                colWidths={[
                  'auto',
                  '5.5rem',
                  '6.5rem',
                  '6rem',
                  '5.5rem',
                  '5.5rem',
                  '9.5rem',
                  '4.5rem',
                  '5.5rem',
                  '7.5rem',
                ]}
              >
                <THead>
                  <tr>
                    <Th>Signal</Th>
                    <Th align="right">
                      <HeaderTip
                        label="Accounts"
                        tip="Every account with the signal, any time: the same count as Signal coverage."
                      />
                    </Th>
                    <Th align="right">
                      <HeaderTip
                        label="Before"
                        tip="Accounts with the signal before their first demo booking. Only these are evidence: a signal at or after the booking cannot have caused it."
                      />
                    </Th>
                    <Th align="right">Converted</Th>
                    <Th align="right">Raw rate</Th>
                    <Th align="right">Estimate</Th>
                    <Th>94% credible</Th>
                    <Th align="right">Lift</Th>
                    <Th align="right">Hand-set</Th>
                    <Th align="right">
                      <HeaderTip
                        label="In use"
                        tip={`The weight scoring uses. ${
                          usedMajority === 'Model'
                            ? 'The model’s weight unless the row says “hand-set”'
                            : usedMajority === 'Hand-set'
                              ? 'The hand-set weight unless the row says “model”'
                              : 'Each row says whether it is the model’s or the hand-set weight'
                        }: the model’s replaces the hand-set one once the estimate rests on enough accounts; outcomes always keep the hand-set weight.`}
                      />
                    </Th>
                  </tr>
                </THead>
                <tbody>
                  {evidenceRows.map((r) => (
                    <ProbabilityRow key={r.signalType} r={r} usedMajority={usedMajority} baseRate={probabilities.data!.baseRate} />
                  ))}
                  {collapsePrior && (
                    <>
                      <tr
                        className="cursor-pointer border-b border-border-light bg-surface-secondary/40 hover:bg-surface-secondary"
                        onClick={() => setShowPriorRows((v) => !v)}
                        aria-expanded={showPriorRows}
                      >
                        <td colSpan={10} className="py-2 px-3 text-body-xs text-text-tertiary">
                          <span className="inline-flex items-center gap-1.5">
                            <ChevronRight
                              className={clsx('h-3.5 w-3.5 transition-transform', showPriorRows && 'rotate-90')}
                              aria-hidden
                            />
                            {priorRows.length} signals use the default prior · {pct(priorRows[0]!.posteriorMean)} ·{' '}
                            {priorRows[0]!.liftRatio.toFixed(2)}× · hand-set weight, no accounts yet
                          </span>
                        </td>
                      </tr>
                      {showPriorRows &&
                        priorRows.map((r) => <ProbabilityRow key={r.signalType} r={r} usedMajority={usedMajority} baseRate={probabilities.data!.baseRate} />)}
                    </>
                  )}
                </tbody>

              </Table>
            </>
          )
        ) : null}
      </Card>

      {/* ── Removal effects ──────────────────────────────────────────── */}
      <Card padding="sm" className="space-y-3">
        <SectionTitle
          title="What each channel is worth"
          info="Removal effect: take the touch paths as a Markov chain, delete a channel, and see how far the chance of conversion falls. It answers what we would lose by switching a channel off, which is a different question from what deserves more budget."
        />

        {removal.isLoading ? (
          <LoadingState />
        ) : removal.isError ? (
          <ErrorState title="Couldn’t load the removal effects" error={removal.error} onRetry={() => removal.refetch()} />
        ) : removal.data ? (
          <>
            {!removal.data.usable && (
              <Alert variant="warning">
                Computed but not driving attribution. {removal.data.reason}
              </Alert>
            )}
            <p className="text-body-sm text-text-secondary">
              <span>
                Path conversion rate{' '}
                <span className="tabular-nums font-medium">{pct(removal.data.baseConversionProbability)}</span>:{' '}
                {formatNumber(removal.data.convertingPaths)} of {formatNumber(removal.data.paths)} touch paths converting.{' '}
                {probabilities.data
                  ? `Counted per path, so it can differ from the account base rate above (${pct(probabilities.data.baseRate)}); here it is ${rateComparison(removal.data.baseConversionProbability, probabilities.data.baseRate)} the account rate.`
                  : 'Counted per path, so it can differ from the account base rate.'}
              </span>
              <InfoTip label="The two base rates" className="ml-1 align-middle">{BASE_RATES_NOTE}</InfoTip>
            </p>
            {removal.data.effects.length === 0 ? (
              <p className="text-body-sm text-text-tertiary">No channel paths on the ledger yet.</p>
            ) : (
              <Table minWidth={640}>
                <THead>
                  <tr>
                    <Th>Channel</Th>
                    <Th align="right">Paths</Th>
                    <Th align="right">Converting</Th>
                    <Th align="right">Removal effect</Th>
                    <Th align="right">Credit share</Th>
                  </tr>
                </THead>
                <tbody>
                  {removal.data.effects.map((e) => (
                    <Tr key={e.channel}>
                      <Td className="text-text-primary">
                        {channelLabel(e.channel)}
                        {utmOnlyPaths.has(e.channel) && (
                          <span
                            className="ml-1.5 text-body-xs text-text-tertiary"
                            title="No signal on this channel writes rows yet; these touches are website visits that arrived through its links (UTM source)."
                          >
                            · via website visits from its links
                          </span>
                        )}
                      </Td>
                      <Td align="right" className="tabular-nums text-text-secondary">{count(e.paths)}</Td>
                      <Td align="right" className="tabular-nums text-text-secondary">{count(e.convertingPaths)}</Td>
                      <Td align="right" className="tabular-nums text-text-secondary">{share(e.removalEffect)}</Td>
                      <Td align="right" className="tabular-nums font-medium text-text-primary">
                        {share(e.creditShare)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        ) : null}
      </Card>
    </div>
  );
}
